import client from './client';
import type { Site, CreateSiteInput, UpdateSiteInput, PaginatedResponse, SiteQueryParams, SiteAccessLog } from '@/types';

export async function getSites(params: SiteQueryParams = {}): Promise<PaginatedResponse<Site>> {
  const { data } = await client.get<PaginatedResponse<Site>>('/sites', { params });
  return data;
}

export async function getSite(id: number): Promise<Site> {
  const { data } = await client.get<Site>(`/sites/${id}`);
  return data;
}

export async function createSite(input: CreateSiteInput): Promise<Site> {
  const { data } = await client.post<Site>('/sites', input);
  return data;
}

export async function updateSite(id: number, input: UpdateSiteInput): Promise<Site> {
  const { data } = await client.put<Site>(`/sites/${id}`, input);
  return data;
}

export async function deleteSite(id: number): Promise<void> {
  await client.delete(`/sites/${id}`);
}

export async function checkSite(id: number): Promise<{ status: string; last_checked_at: string }> {
  const { data } = await client.post<{ status: string; last_checked_at: string }>(`/sites/${id}/check`);
  return data;
}

export async function toggleFavorite(id: number): Promise<Site> {
  const { data } = await client.patch<Site>(`/sites/${id}/favorite`);
  return data;
}

export async function getQuickAccessSites(limit = 10): Promise<{ data: Site[] }> {
  const { data } = await client.get<{ data: Site[] }>('/sites/quick', { params: { limit } });
  return data;
}

export async function updateQuickAccessOrder(sites: Array<{ id: number; order: number }>): Promise<{ data: Site[] }> {
  const { data } = await client.put<{ data: Site[] }>('/sites/quick', { sites });
  return data;
}

export async function getCategories(): Promise<{ data: string[] }> {
  const { data } = await client.get<{ data: string[] }>('/sites/categories');
  return data;
}

export async function logAccess(id: number): Promise<{ url: string }> {
  const { data } = await client.post<{ url: string }>(`/sites/${id}/access`);
  return data;
}

export async function getAccessLogs(id: number, page = 1, limit = 50): Promise<{ data: SiteAccessLog[]; meta: { total: number } }> {
  const { data } = await client.get(`/sites/${id}/logs`, { params: { page, limit } });
  return data;
}

export async function generateShareToken(id: number): Promise<{ token: string; url: string }> {
  const { data } = await client.post<{ token: string; url: string }>(`/sites/${id}/share`);
  return data;
}

export async function removeShareToken(id: number): Promise<void> {
  await client.delete(`/sites/${id}/share`);
}

export const sitesApi = {
  getSites,
  getSite,
  createSite,
  updateSite,
  deleteSite,
  checkSite,
  toggleFavorite,
  getQuickAccessSites,
  updateQuickAccessOrder,
  getCategories,
  logAccess,
  getAccessLogs,
  generateShareToken,
  removeShareToken,
};

export default sitesApi;
